import _find from 'lodash/find';
import {type FC, Fragment, type MouseEvent, memo, Suspense} from 'react';
import {GalleryLoadingOverlay} from '~/GalleryPB/components/GalleryLoadingOverlay/GalleryLoadingOverlay';
import {NoResultsHeader} from '~/GalleryPB/components/NoResultsHeader/NoResultsHeader';
import {TemplateRow} from '~/GalleryPB/components/TemplateRow';
import type {GalleryPromoData, GalleryPromoTileConfig} from '~/GalleryPB/types/gallery';
import {type Template, VariantType} from '~/GalleryPB/types/template';
import {Link} from '~library/atoms/Link';
import {Typography} from '~library/atoms/Typography';
import {useIsMediumAndUp} from '~library/hooks/useMatchQuery';

import * as styles from './GalleryResults.module.scss';

const freeFirstCategories = ['halloween'];

function* generateRows(
  rowSize: number,
  premiums: Template[],
  frees: Template[],
  galleryPromoTile: GalleryPromoTileConfig,
  categoryId?: string
): Generator<(Template | GalleryPromoTileConfig)[]> {
  let f = 0;
  let p = 0;
  let premiumRow = categoryId && !freeFirstCategories.includes(categoryId);
  let done = false;
  let row: (Template | GalleryPromoTileConfig)[] = [];
  let promoTileInserted = false;
  while (!done) {
    /* Insert Promo Tile in the 7 and 8 spot of the results */
    if (galleryPromoTile?.src && f + p === 6 && !promoTileInserted) {
      row.push(galleryPromoTile);
      promoTileInserted = true;
    } else if (p < premiums.length && premiumRow) {
      row.push({...premiums[p], pos: p});
      p++;
    } else if (f < frees.length) {
      row.push({...frees[f], pos: f});
      f++;
    } else if (p < premiums.length) {
      row.push({...premiums[p], pos: p});
      p++;
    } else done = true;
    if (row.length === rowSize || (done && row.length > 0)) {
      yield row;
      row = [];
      premiumRow = !premiumRow;
    }
  }
}

interface GalleryResultsProps {
  premiumTemplates: Template[];
  freeTemplates: Template[];
  showNoResultsHeader?: boolean;
  favorites: string[];
  categoryId?: string;
  onTemplateClick: (e: MouseEvent, templateId: string, href: string) => void;
  onClickQuickView: (templateId: string) => void;
  onClearFilters: () => void;
  isLoading?: boolean;
}

export const GalleryResults: FC<GalleryResultsProps> = memo(
  ({
    premiumTemplates,
    freeTemplates,
    showNoResultsHeader = false,
    favorites,
    categoryId,
    onTemplateClick,
    onClickQuickView,
    onClearFilters,
    isLoading = false,
  }) => {
    const mediumScreens = useIsMediumAndUp();
    const galleryPromoTile = window.gallery_promo_tile; // TODO: don't use the window for this data
    let {gallery_promo_row: promos = [] as (GalleryPromoData & {order: number})[]} = window;

    if (!Array.isArray(promos)) {
      // Wrap legacy gallery_promo_rows object in an array Can remove once
      // EVT-30318 is released.
      promos = [{...promos, order: 1}];
    }

    if (!mediumScreens) {
      // EVT-30138 - doubles the order for mobile devices since original ordering
      // was after first row for desktop and after the second row for mobile
      promos = promos.map(({order, ...promo}) => ({...promo, order: order * 2}));
    }

    const ROW_COUNT = mediumScreens
      ? parseInt(
          document.documentElement.style.getPropertyValue('--gallery-templates-per-row-desktop')
        )
      : 2;

    const templateRows = [
      ...generateRows(ROW_COUNT, premiumTemplates, freeTemplates, galleryPromoTile, categoryId),
    ];

    function renderTemplateRow(row: (Template | GalleryPromoTileConfig)[], index: number) {
      return (
        <Suspense fallback={<div />} key={`galleryRow_${index + 1}`}>
          <TemplateRow
            results={row}
            type={VariantType.FREE}
            favorites={favorites}
            screen={mediumScreens ? 'desktop' : 'mw'}
            categoryId={categoryId}
            onTemplateClick={onTemplateClick}
            loading={index < 2 ? 'eager' : 'lazy'}
            onClickQuickView={onClickQuickView}
            rowCount={ROW_COUNT}
          />
        </Suspense>
      );
    }

    const bottomAd = _find(promos, ({order}) => order < 0);

    return (
      <div className={styles.galleryResultsWrapper} data-qa-id="galleryResultsGrid">
        <GalleryLoadingOverlay show={isLoading} />
        {showNoResultsHeader && (
          <>
            <NoResultsHeader onClearFilters={onClearFilters} />
            <Typography variant="header6" className={styles.suggestedTemplatesHeader}>
              Not sure where to start? Try one of these.
            </Typography>
          </>
        )}
        {templateRows.map((row, index) => {
          const {order, ...promo} = _find(promos, {order: index}) || {};

          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: existing error
            <Fragment key={index}>
              {order === index && 'href' in promo && <GalleryPromo promo={{...promo, order}} />}
              {renderTemplateRow(row, index)}
            </Fragment>
          );
        })}
        {bottomAd && <GalleryPromo promo={bottomAd} />}
      </div>
    );
  }
);

interface GalleryPromoProps {
  promo: GalleryPromoData & {order: number};
}

const GalleryPromo = ({promo}: GalleryPromoProps) => {
  const mediumScreens = useIsMediumAndUp();
  const [width, height] = mediumScreens
    ? [promo.width, promo.height]
    : [promo.mobile_width, promo.mobile_height];
  return (
    !!Object.keys(promo).length && (
      <Fragment key={promo.src + promo.order}>
        <Link
          href={promo.href}
          data-qa-id={promo['data-qa-id'] || 'gallery-promo-row'}
          className={styles.galleryPromoRow}
          key="galleryPromoRow"
        >
          <img
            className={styles.galleryPromoRowImage}
            src={mediumScreens ? promo.src : promo.mobile_src}
            alt={promo.alt}
            width={width}
            height={height}
          />
        </Link>
      </Fragment>
    )
  );
};
